(function () {
  /** @type {boolean} */
  let loaded = false;
  /** @type {boolean} */
  let loading = false;
  /** @type {boolean} */
  let configuring = false;
  /** @type {boolean} WIL-646: true once the visitor has interacted, which also seals LCP. */
  let userHasInteracted = false;
  /** @type {null | { jump: Function, getCurrentFrame: Function }} */
  let viewer = null;

  const OPTIONS = {
    'autospin-direction': 'clockwise',
    columns: 36,
    rows: 1,
    magnify: true,
    'magnifier-width': '60%',
    'magnifier-shape': 'inner',
    fullscreen: true,
    spin: 'drag',
    speed: 50,
    'mousewheel-step': 1,
    'autospin-speed': 3600,
    smoothing: true,
    autospin: 'off',
    'initialize-on': 'load',
    'start-column': 1,
    'start-row': 'auto',
    'loop-column': true,
    'loop-row': false,
    'reverse-column': false,
    'reverse-row': false,
    'column-increment': 1,
    'row-increment': 1,
    hint: false,
  };

  const LANG = {
    'loading-text': 'Loading...',
    'fullscreen-loading-text': 'Loading large spin...',
    'hint-text': 'Drag to spin',
    'mobile-hint-text': 'Swipe to spin',
  };

  /** @param {string} href */
  function ensureStylesheet(href) {
    if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  /** @param {string} src */
  function loadScript(src) {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(script);
    });
  }

  /** @param {HTMLElement} root */
  function readAssetUrls(root) {
    return {
      css: root.dataset.magic360Css?.split('|').filter(Boolean) ?? [],
      js: root.dataset.magic360Js ?? '',
    };
  }

  /**
   * Give Magic360 a box that matches the photo’s aspect ratio, centered by
   * the flex stage. Do not touch the canvas — Magic360 draws into it.
   */
  function fitMagic360ToStage() {
    document.querySelectorAll('[data-product-media-stage]').forEach((stage) => {
      if (!(stage instanceof HTMLElement)) return;

      const fullscreen = document.fullscreenElement;
      const magicNode = stage.querySelector('.Magic360');
      const box =
        fullscreen instanceof HTMLElement &&
        (fullscreen === stage ||
          stage.contains(fullscreen) ||
          (magicNode instanceof Node && fullscreen.contains(magicNode)))
          ? fullscreen
          : stage;
      const stageWidth = box.clientWidth;
      const stageHeight = box.clientHeight;
      if (stageWidth < 1 || stageHeight < 1) return;

      const container = stage.querySelector('.Magic360-container');
      const magic = stage.querySelector('.Magic360');
      const anchor = stage.querySelector('.product-page__magic360-anchor');
      const wrapper =
        container instanceof HTMLElement
          ? container
          : magic instanceof HTMLElement
            ? magic
            : anchor;
      if (!(wrapper instanceof HTMLElement)) return;

      const sourceImg = wrapper.querySelector('img');
      let photoWidth = 0;
      let photoHeight = 0;
      if (sourceImg instanceof HTMLImageElement) {
        photoWidth = sourceImg.naturalWidth;
        photoHeight = sourceImg.naturalHeight;
        if (!sourceImg.complete || photoWidth < 1) {
          sourceImg.addEventListener('load', fitMagic360ToStage, { once: true });
        }
      }
      if (photoWidth < 1 || photoHeight < 1) return;

      const scale = Math.min(stageWidth / photoWidth, stageHeight / photoHeight);
      const boxWidth = Math.max(1, Math.round(photoWidth * scale));
      const boxHeight = Math.max(1, Math.round(photoHeight * scale));
      const wrapperIsFullscreen = fullscreen instanceof HTMLElement && wrapper === fullscreen;
      const sized = wrapperIsFullscreen && magic instanceof HTMLElement ? magic : wrapper;

      if (!wrapperIsFullscreen) {
        wrapper.style.setProperty('position', 'relative', 'important');
        wrapper.style.setProperty('inset', 'auto', 'important');
        wrapper.style.setProperty('top', 'auto', 'important');
        wrapper.style.setProperty('left', 'auto', 'important');
        wrapper.style.setProperty('right', 'auto', 'important');
        wrapper.style.setProperty('bottom', 'auto', 'important');
        wrapper.style.setProperty('display', 'block', 'important');
        wrapper.style.setProperty('width', `${boxWidth}px`, 'important');
        wrapper.style.setProperty('height', `${boxHeight}px`, 'important');
        wrapper.style.setProperty('max-width', '100%', 'important');
        wrapper.style.setProperty('max-height', '100%', 'important');
        wrapper.style.setProperty('margin', '0', 'important');
        wrapper.style.setProperty('transform', 'none', 'important');
      }

      [sized, magic, anchor].forEach((layer) => {
        if (!(layer instanceof HTMLElement)) return;
        if (layer === wrapper && wrapperIsFullscreen) return;
        layer.style.setProperty('position', 'relative', 'important');
        layer.style.setProperty('inset', 'auto', 'important');
        layer.style.setProperty('top', 'auto', 'important');
        layer.style.setProperty('left', 'auto', 'important');
        layer.style.setProperty('width', layer === sized ? `${boxWidth}px` : '100%', 'important');
        layer.style.setProperty('height', layer === sized ? `${boxHeight}px` : '100%', 'important');
        layer.style.setProperty('transform', 'none', 'important');
        layer.style.setProperty('margin', layer === sized && wrapperIsFullscreen ? 'auto' : '0', 'important');
      });
    });
  }

  /** Remove Magic360's own hint. The theme "Drag to spin" pill stays. */
  function stripMagic360Chrome() {
    document.querySelectorAll('[data-magic360-root]').forEach((root) => {
      if (!(root instanceof HTMLElement)) return;
      root.querySelectorAll('.m360-hint').forEach((node) => {
        node.remove();
      });
    });
  }

  function scheduleFit() {
    fitMagic360ToStage();
    requestAnimationFrame(fitMagic360ToStage);
    window.setTimeout(fitMagic360ToStage, 50);
    window.setTimeout(fitMagic360ToStage, 250);
    window.setTimeout(fitMagic360ToStage, 800);
  }

  /**
   * The anchor Magic360 will enhance. Class `Magic360` is removed until the
   * first input: v4.6.10 calls start() on domready as the script evaluates,
   * and that rebuilds the photo. Without the class, auto-start finds nothing.
   * @param {HTMLElement} root
   */
  function spinAnchor(root) {
    const anchor = root.querySelector('.product-page__magic360-anchor, a.Magic360');
    return anchor instanceof HTMLElement ? anchor : null;
  }

  /** @param {HTMLElement} root */
  function disarmAutoStart(root) {
    const anchor = spinAnchor(root);
    if (!anchor || anchor.dataset.magic360Disarmed === 'true') return;
    anchor.classList.remove('Magic360');
    anchor.dataset.magic360Disarmed = 'true';
  }

  /** @param {HTMLElement} root */
  function rearm(root) {
    const anchor = spinAnchor(root);
    if (!anchor) return null;
    anchor.classList.add('Magic360');
    delete anchor.dataset.magic360Disarmed;
    return anchor;
  }

  /** @param {HTMLElement | null} anchor */
  function columnCount(anchor) {
    const raw = anchor?.getAttribute('data-magic360-options') || '';
    const match = raw.match(/columns:\s*(\d+)/);
    const count = match ? Number(match[1]) : OPTIONS.columns;
    return count > 0 ? count : OPTIONS.columns;
  }

  /** @param {HTMLElement} root */
  function pixelsPerColumn(root) {
    const stage = root.querySelector('[data-product-media-stage]');
    const width = stage instanceof HTMLElement && stage.clientWidth > 0 ? stage.clientWidth : root.clientWidth;
    return Math.max(8, width / columnCount(spinAnchor(root)));
  }

  /** @param {HTMLElement} root */
  function markReady(root) {
    root.dataset.magic360Ready = 'true';
    document.getElementById('magic-spin-a')?.addEventListener('click', (event) => {
      event.preventDefault();
    });
    document.dispatchEvent(new CustomEvent('magic360:ready'));
  }

  /**
   * Build the viewer. Only call once the visitor has interacted — start()
   * repaints the gallery. Returns the instance start() creates; its own drag
   * listeners attach on a delay, so they miss the pointerdown that woke it.
   * @param {HTMLElement} root
   */
  function beginViewer(root) {
    if (root.dataset.magic360Ready === 'true') return viewer;
    if (!window.Magic360 || typeof window.Magic360.start !== 'function' || configuring) return viewer;
    configuring = true;
    window.Magic360.options = OPTIONS;
    window.Magic360.lang = LANG;
    readAssetUrls(root).css.forEach(ensureStylesheet);
    rearm(root);
    fitMagic360ToStage();
    try {
      const started = window.Magic360.start();
      if (started) viewer = started;
    } catch (error) {
      console.warn('[magic360-loader] start', error);
    }
    stripMagic360Chrome();
    scheduleFit();
    markReady(root);
    configuring = false;
    return viewer;
  }

  /**
   * Fetch and evaluate Magic360 without starting a viewer. The script is
   * parsed early so the first drag is not waiting on the network.
   * @param {HTMLElement} root
   */
  function warmScript(root) {
    if (loaded) {
      if (userHasInteracted && root.dataset.magic360Ready !== 'true') beginViewer(root);
      return;
    }
    if (loading) return;
    loading = true;
    disarmAutoStart(root);
    loadScript(readAssetUrls(root).js)
      .then(() => {
        loaded = true;
        loading = false;
        if (userHasInteracted) beginViewer(root);
      })
      .catch((error) => {
        loading = false;
        console.warn('[magic360-loader]', error);
      });
  }

  /**
   * Apply the gesture that woke the viewer. Magic360 binds pointerdown about
   * 300ms after start(), so a phone's first tap-drag would otherwise be lost.
   * @param {HTMLElement} root
   * @param {PointerEvent} startEvent
   */
  function followGesture(root, startEvent) {
    const pointerId = startEvent.pointerId;
    const originX = startEvent.clientX;
    let latestX = originX;
    let shown = 0;
    let stopped = false;

    const paint = () => {
      const current = viewer;
      if (!current || typeof current.jump !== 'function' || typeof current.getCurrentFrame !== 'function') return;
      const want = Math.round((originX - latestX) / pixelsPerColumn(root));
      if (want === shown) return;
      let before;
      try {
        before = current.getCurrentFrame();
      } catch {
        return;
      }
      if (!before) return;
      try {
        current.jump(before.row - 1, before.col - 1 + (want - shown));
      } catch {
        return;
      }
      let after;
      try {
        after = current.getCurrentFrame();
      } catch {
        return;
      }
      if (!after || after.col === before.col) return;
      const columns = columnCount(spinAnchor(root));
      let step = after.col - before.col;
      if (step > columns / 2) step -= columns;
      if (step < -columns / 2) step += columns;
      shown += step;
    };

    const onMove = (event) => {
      if (!(event instanceof PointerEvent) || event.pointerId !== pointerId) return;
      latestX = event.clientX;
      paint();
    };
    const finish = (event) => {
      if (stopped) return;
      if (event instanceof PointerEvent && event.pointerId !== pointerId) return;
      stopped = true;
      if (event instanceof PointerEvent) latestX = event.clientX;
      paint();
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerup', finish, true);
      window.removeEventListener('pointercancel', finish, true);
      window.setTimeout(paint, 350);
      window.setTimeout(() => {
        window.clearInterval(timer);
        paint();
      }, 800);
    };
    const timer = window.setInterval(paint, 50);
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', finish, true);
    window.addEventListener('pointercancel', finish, true);
  }

  /** @param {Event} event */
  function onPointerDown(event) {
    if (!(event instanceof PointerEvent) || !(event.target instanceof Element)) return;
    const root = event.target.closest('[data-magic360-root]');
    if (!(root instanceof HTMLElement)) return;
    userHasInteracted = true;
    const onSpin = event.target.closest('[data-magic360-slide], .product-page__magic360-anchor, a.Magic360');
    if (onSpin && root.dataset.magic360Ready !== 'true') followGesture(root, event);
    if (loaded) beginViewer(root);
    else warmScript(root);
  }

  /**
   * Run `handler` once the visitor has interacted, so anything it repaints
   * cannot become a late Largest Contentful Paint candidate. LCP stops being
   * recorded at the first user input, so this is the cheapest way to keep a
   * deferred enhancement out of the measurement without delaying it in
   * practice — a scroll is enough. Fires immediately if input already happened.
   * @param {() => void} handler
   */
  function afterFirstInput(handler) {
    if (userHasInteracted) {
      handler();
      return;
    }
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'];
    const run = () => {
      events.forEach((name) => window.removeEventListener(name, run, true));
      userHasInteracted = true;
      handler();
    };
    events.forEach((name) => window.addEventListener(name, run, { once: true, passive: true, capture: true }));
  }

  /** @param {HTMLElement} root */
  function bindTriggers(root) {
    // Parse the library immediately. Do not call start() here: that repaints
    // the gallery and re-opens the WIL-646 LCP regression. Scroll still
    // starts the viewer, because that input has already sealed LCP.
    warmScript(root);

    document.addEventListener('magic360:load', () => {
      userHasInteracted = true;
      if (loaded) beginViewer(root);
      else warmScript(root);
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            afterFirstInput(() => {
              if (loaded) beginViewer(root);
              else warmScript(root);
            });
          });
        },
        { rootMargin: '120px 0px' }
      );
      observer.observe(root);
    }
  }

  function boot() {
    fitMagic360ToStage();
    document.querySelectorAll('[data-magic360-root]:not([data-magic360-init])').forEach((root) => {
      if (!(root instanceof HTMLElement)) return;
      root.dataset.magic360Init = 'true';
      bindTriggers(root);
    });
    if (window.__mapMagic360Pointer) {
      window.removeEventListener('pointerdown', window.__mapMagic360Pointer, true);
    }
    window.__mapMagic360Pointer = onPointerDown;
    window.addEventListener('pointerdown', onPointerDown, true);
  }

  let resizeTimer;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(fitMagic360ToStage, 150);
  });

  document.addEventListener('magic360:ready', fitMagic360ToStage);

  document.addEventListener('fullscreenchange', () => {
    scheduleFit();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
